import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { api, ApiError } from '@/api/client';
import { AnnouncementPicture, PICK_MAX_BYTES, PICTURE_TYPES, shrinkPicture } from '@/components/AnnouncementPicture';
import type { FeedItem } from '@/components/announcementFeed';
import { DraftNotice } from '@/components/DraftNotice';
import { ErrorNote, Field, Modal, TextArea } from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { confirmDiscard } from '@/stores/confirm';
import { useDraft } from '@/stores/drafts';
import { useUnsavedFlag } from '@/stores/unsaved';
import { AnnouncementPreview } from './AnnouncementPreview';
import {
  AudiencePicker,
  EVERYONE,
  audiencePayload,
  audienceProblem,
  describeAudience,
  type Audience,
  type AudienceOptions,
} from './AudiencePicker';
import { WhenPicker } from './WhenPicker';
import { defaultWhen, describeWhen, resolveWhen, whenFromSaved, type WhenValue } from './when';

type Priority = 'INFO' | 'IMPORTANT' | 'URGENT';

const PRIORITIES: { value: Priority; label: string; hint: string }[] = [
  { value: 'INFO', label: 'Info', hint: 'Plain text in the strip. People can hide it once read.' },
  { value: 'IMPORTANT', label: 'Important', hint: 'Marked Important and shown before plain notices.' },
  { value: 'URGENT', label: 'Urgent', hint: 'Red, shown first, and nobody can hide it until it comes down.' },
];

/** The part of the form that is typed or chosen, kept as a local draft. */
interface ComposerValues {
  headlineEn: string;
  headlineUr: string;
  bodyEn: string;
  bodyUr: string;
  priority: Priority;
  audience: Audience;
  when: WhenValue;
  urdu: boolean;
}

type PictureChange =
  | { kind: 'keep' }
  | { kind: 'new'; blob: Blob; previewUrl: string }
  | { kind: 'remove' };

type Existing = Awaited<ReturnType<typeof fetchDetail>>;

function fetchDetail(id: string) {
  return api.get(`/api/v1/announcements/${id}` as '/api/v1/announcements/{id}');
}

/**
 * Writing an announcement: a headline, maybe a message or a picture, and
 * Publish now. Who sees it and when are sensible defaults shown as one line
 * each, opened only by someone who wants to change them; the preview beside
 * the form shows exactly what people will get.
 */
export function AnnouncementComposer({
  existingId,
  onClose,
  onSaved,
}: {
  existingId?: string | undefined;
  onClose: () => void;
  /** After a save; `message` says what happened in plain words. */
  onSaved: (message: string) => void;
}) {
  const options = useQuery({
    queryKey: ['announcements', 'audience-options'],
    queryFn: () => api.get('/api/v1/announcements/audience-options'),
  });
  const detail = useQuery({
    queryKey: ['announcements', existingId],
    queryFn: () => fetchDetail(existingId!),
    enabled: !!existingId,
    // Always the latest saved version, never what the list showed earlier.
    staleTime: 0,
  });

  const title = existingId ? 'Edit announcement' : 'New announcement';
  const failed = options.error ?? detail.error;

  if (failed || !options.data || (existingId && !detail.data)) {
    return (
      <Modal title={title} onClose={onClose} wide className="composer">
        <h2>{title}</h2>
        {failed ? (
          <ErrorNote message={failed instanceof Error ? failed.message : 'Could not load the announcement.'} />
        ) : (
          <p className="muted">Loading…</p>
        )}
        <div className="toolbar">
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <ComposerForm
      title={title}
      existing={existingId ? detail.data : undefined}
      options={options.data}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function initialValues(existing: Existing | undefined, options: AudienceOptions, allFarmsAllowed: boolean): ComposerValues {
  if (existing) {
    return {
      headlineEn: existing.headlineEn,
      headlineUr: existing.headlineUr ?? '',
      bodyEn: existing.bodyEn ?? '',
      bodyUr: existing.bodyUr ?? '',
      priority: existing.priority as Priority,
      audience: {
        allRoles: existing.audience.allRoles,
        roleCodes: existing.audience.roleCodes,
        allFarms: existing.audience.allFarms,
        farmIds: existing.audience.farmIds,
      },
      when: whenFromSaved(existing.startsAt, existing.endsAt),
      urdu: !!(existing.headlineUr || existing.bodyUr),
    };
  }
  return {
    headlineEn: '',
    headlineUr: '',
    bodyEn: '',
    bodyUr: '',
    priority: 'INFO',
    // Someone who looks after some farms starts with all of theirs, since
    // "every farm" is not theirs to choose.
    audience: allFarmsAllowed ? EVERYONE : { ...EVERYONE, allFarms: false, farmIds: options.farms.map((f) => f.id) },
    when: defaultWhen(),
    urdu: false,
  };
}

function ComposerForm({
  title,
  existing,
  options,
  onClose,
  onSaved,
}: {
  title: string;
  existing: Existing | undefined;
  options: AudienceOptions;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { user, can } = useAuth();
  const canPost = can('com.announcement.post');
  const allFarmsAllowed = user?.scope.allFarms ?? false;

  const initial = useMemo(
    () => initialValues(existing, options, allFarmsAllowed),
    // Only once per open: later refetches must not reset what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [values, setValues] = useState<ComposerValues>(initial);
  const set = <K extends keyof ComposerValues>(key: K, value: ComposerValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  // What has been saved so far in this sitting. A first save creates the
  // announcement; anything after it (a retry after the picture failed) must
  // update that one, never create a second.
  const [savedId, setSavedId] = useState<string | undefined>(existing?.id);
  const [version, setVersion] = useState<number>(existing?.version ?? 0);
  const [savedImageKey, setSavedImageKey] = useState<string | null>(existing?.imageKey ?? null);

  const [picture, setPicture] = useState<PictureChange>({ kind: 'keep' });
  const [preparing, setPreparing] = useState(false);
  const [pictureError, setPictureError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const [open, setOpen] = useState<'audience' | 'when' | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState<'publish' | 'draft' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const draft = useDraft<ComposerValues>(
    existing ? `announcement.edit.${existing.id}` : 'announcement.new',
    values,
    (saved) => setValues({ ...initial, ...saved }),
  );

  // The preview's object URL goes when it is replaced or the form closes.
  useEffect(() => {
    if (picture.kind !== 'new') return;
    return () => URL.revokeObjectURL(picture.previewUrl);
  }, [picture]);

  const dirty = JSON.stringify(values) !== JSON.stringify(initial) || picture.kind !== 'keep';
  useUnsavedFlag(dirty);

  // Modal re-runs its focus setup whenever onClose changes, so this stays one
  // function for the dialog's whole life and reads the latest state by ref.
  const latest = useRef({ dirty, draft, onClose });
  latest.current = { dirty, draft, onClose };
  const close = useCallback(() => {
    void (async () => {
      const { dirty: isDirty, draft: d, onClose: done } = latest.current;
      if (isDirty && !(await confirmDiscard())) return;
      d.forget();
      done();
    })();
  }, []);

  // ---- what the choices mean
  const state = existing?.state;
  const isPublished = state === 'LIVE' || state === 'SCHEDULED' || state === 'EXPIRED';
  const resolved = resolveWhen(values.when);
  const audienceIssue = audienceProblem(values.audience);
  const headlineIssue = values.headlineEn.trim() ? null : 'Write a headline. It is what scrolls across the top.';
  const problem = headlineIssue ?? audienceIssue ?? resolved.problem;

  const primary: { label: string; mode: 'publish' | 'save' | 'draft' } = !canPost
    ? { label: 'Save draft', mode: 'draft' }
    : isPublished
      ? { label: 'Save changes', mode: 'save' }
      : values.when.start === 'later'
        ? { label: 'Schedule', mode: 'publish' }
        : { label: 'Publish now', mode: 'publish' };
  const offerDraft = canPost && !isPublished;

  // ---- picture
  async function takePicture(file: File | undefined) {
    if (!file) return;
    if (!PICTURE_TYPES.includes(file.type)) {
      setPictureError('The picture must be a JPEG, PNG or WebP image.');
      return;
    }
    if (file.size > PICK_MAX_BYTES) {
      setPictureError('This file is too big to open. Use a picture under 25 MB.');
      return;
    }
    setPreparing(true);
    setPictureError(null);
    try {
      const blob = await shrinkPicture(file);
      setPicture({ kind: 'new', blob, previewUrl: URL.createObjectURL(blob) });
    } catch (err) {
      setPictureError(err instanceof Error ? err.message : 'The picture could not be prepared.');
    } finally {
      setPreparing(false);
    }
  }

  const showsPicture = picture.kind === 'new' || (picture.kind === 'keep' && !!savedImageKey);
  const pictureSrc = picture.kind === 'new' ? picture.previewUrl : undefined;

  // ---- saving
  async function submit(mode: 'publish' | 'save' | 'draft') {
    setAttempted(true);
    if (problem) {
      if (audienceIssue) setOpen('audience');
      else if (resolved.problem) setOpen('when');
      return;
    }
    setBusy(mode === 'draft' ? 'draft' : 'publish');
    setError(null);

    const payload = {
      headlineEn: values.headlineEn.trim(),
      headlineUr: values.urdu ? values.headlineUr.trim() : '',
      bodyEn: values.bodyEn.trim(),
      bodyUr: values.urdu ? values.bodyUr.trim() : '',
      priority: values.priority,
      startsAt: resolved.startsAt,
      endsAt: resolved.endsAt,
      ...audiencePayload(values.audience),
    };

    let id = savedId;
    let v = version;
    let stage: 'text' | 'picture' | 'publish' = 'text';
    try {
      if (!id) {
        id = (await api.post<{ id: string }>('/api/v1/announcements', payload)).id;
        v = 1;
        setSavedId(id);
      } else {
        await api.patch(`/api/v1/announcements/${id}`, { ...payload, version: v });
        v += 1;
      }
      setVersion(v);

      stage = 'picture';
      if (picture.kind === 'new') {
        const saved = await api.upload<{ imageKey: string }>(`/api/v1/announcements/${id}/image`, picture.blob);
        v += 1;
        setVersion(v);
        setSavedImageKey(saved.imageKey);
        setPicture({ kind: 'keep' });
      } else if (picture.kind === 'remove' && savedImageKey) {
        await api.delete(`/api/v1/announcements/${id}/image`);
        v += 1;
        setVersion(v);
        setSavedImageKey(null);
        setPicture({ kind: 'keep' });
      }

      stage = 'publish';
      if (mode === 'publish') {
        await api.post(`/api/v1/announcements/${id}/publish`);
      }

      draft.forget();
      onSaved(
        mode === 'draft'
          ? 'Saved as a draft. Nobody sees it until it is published.'
          : mode === 'save'
            ? 'Saved. Dashboards show the change within a minute.'
            : values.when.start === 'later'
              ? `Scheduled. ${describeWhen(values.when)}.`
              : 'Published. It reaches dashboards within a minute.',
      );
    } catch (err) {
      const why = err instanceof Error ? err.message : 'Something went wrong.';
      if (err instanceof ApiError && err.status === 409) {
        setError('Someone else changed this announcement while you were editing. Close it and open it again to see their version.');
      } else if (stage === 'text') {
        setError(`It could not be saved: ${why}`);
      } else if (stage === 'picture') {
        setError(
          `The text is saved${mode === 'publish' && !isPublished ? ' as a draft, not published yet' : ''}, but the picture did not upload: ${why} Press ${primary.label} again to retry.`,
        );
      } else {
        setError(`It is saved as a draft but could not be published: ${why} Press ${primary.label} again to retry.`);
      }
    } finally {
      setBusy(null);
    }
  }

  // ---- preview
  const previewItem: FeedItem = {
    id: savedId ?? 'new',
    headlineEn: values.headlineEn.trim(),
    headlineUr: null,
    bodyEn: values.bodyEn.trim() || null,
    bodyUr: null,
    priority: values.priority,
    startsAt: resolved.startsAt,
    endsAt: resolved.endsAt,
    // Filled in by the preview as they will be at the moment it goes live.
    publishedAt: null,
    postedBy: null,
    canDismiss: values.priority !== 'URGENT',
    imageKey: picture.kind === 'new' ? 'new' : picture.kind === 'remove' ? null : savedImageKey,
  };

  const footer = (
    <div className="composer__footer">
      <div className="composer__status" aria-live="polite">
        {attempted && problem ? (
          <span className="composer__problem">{problem}</span>
        ) : isPublished ? (
          <span className="muted">This announcement is live. Changes reach dashboards within a minute.</span>
        ) : (
          <span className="muted">Your typing is kept on this computer until you save.</span>
        )}
      </div>
      <div className="composer__buttons">
        <button type="button" className="btn" onClick={close} disabled={!!busy}>
          Cancel
        </button>
        {offerDraft && (
          <button type="button" className="btn" onClick={() => void submit('draft')} disabled={!!busy || preparing}>
            {busy === 'draft' ? 'Saving…' : 'Save as draft'}
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void submit(primary.mode)}
          disabled={!!busy || preparing}
        >
          {busy === 'publish' ? 'Working…' : primary.label}
        </button>
      </div>
    </div>
  );

  const importance = PRIORITIES.find((p) => p.value === values.priority)!;

  return (
    <Modal title={title} onClose={close} wide className="composer" footer={footer}>
      <h2 className="composer__title">{title}</h2>
      <DraftNotice
        draft={draft}
        onStartAgain={() => {
          setValues(initial);
          setPicture({ kind: 'keep' });
        }}
      />
      {draft.restoredAt && picture.kind === 'keep' && !savedImageKey && (
        <p className="settings__hint">A picture is not kept with unsaved work. Add it again if you had one.</p>
      )}

      <div className="composer__body">
        <div className="composer__write">
          <Field
            label="Headline"
            required
            value={values.headlineEn}
            onChange={(v) => set('headlineEn', v)}
            maxLength={200}
            placeholder="e.g. Vaccination drive on all sheds this Friday"
            hint={values.headlineEn.length > 160 ? `${values.headlineEn.length}/200` : 'Short and clear: it scrolls across the top of every dashboard.'}
            invalid={attempted && !!headlineIssue}
            span="full"
          />
          <TextArea
            label="Message (optional)"
            value={values.bodyEn}
            onChange={(v) => set('bodyEn', v)}
            rows={3}
          />

          <div
            className={`composer__drop${dragOver ? ' composer__drop--over' : ''}${showsPicture ? ' composer__drop--filled' : ''}`}
            onDragOver={(e: DragEvent) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e: DragEvent) => {
              e.preventDefault();
              setDragOver(false);
              void takePicture(e.dataTransfer.files[0]);
            }}
          >
            {showsPicture ? (
              <AnnouncementPicture
                id={savedId ?? 'new'}
                imageKey={savedImageKey}
                src={pictureSrc}
                alt="The picture on this announcement"
                className="composer__thumb"
              />
            ) : (
              <span className="composer__dropicon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="28" height="28">
                  <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="8.5" cy="9.5" r="1.8" fill="currentColor" />
                  <path d="M4 18l5-5 3.5 3.5L15 14l5 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              </span>
            )}
            <div className="composer__dropwords">
              {preparing ? (
                <strong>Preparing the picture…</strong>
              ) : showsPicture ? (
                <strong>{picture.kind === 'new' ? 'New picture, saved when you publish' : 'Picture'}</strong>
              ) : (
                <>
                  <strong>Add a picture (optional)</strong>
                  <span className="muted">A poster or a photo of a notice. Drop it here, or browse.</span>
                </>
              )}
              <div className="composer__dropactions">
                <label className="btn btn--sm announcementpicture__pick" aria-disabled={preparing || !!busy}>
                  {showsPicture ? 'Replace' : 'Browse…'}
                  <input
                    type="file"
                    accept={PICTURE_TYPES.join(',')}
                    className="sr-only"
                    disabled={preparing || !!busy}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      void takePicture(file);
                    }}
                  />
                </label>
                {showsPicture && (
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost"
                    onClick={() => setPicture(savedImageKey ? { kind: 'remove' } : { kind: 'keep' })}
                    disabled={preparing || !!busy}
                  >
                    Remove
                  </button>
                )}
                {picture.kind === 'remove' && (
                  <button type="button" className="linkbutton" onClick={() => setPicture({ kind: 'keep' })}>
                    Undo remove
                  </button>
                )}
              </div>
            </div>
          </div>
          {pictureError && <ErrorNote message={pictureError} />}

          {values.urdu ? (
            <div className="composer__urdu">
              <Field label="Headline in Urdu" urdu value={values.headlineUr} onChange={(v) => set('headlineUr', v)} maxLength={200} span="full" />
              <TextArea label="Message in Urdu" urdu value={values.bodyUr} onChange={(v) => set('bodyUr', v)} rows={3} />
              <button
                type="button"
                className="linkbutton"
                onClick={() => setValues((v) => ({ ...v, urdu: false, headlineUr: '', bodyUr: '' }))}
              >
                Remove the Urdu version
              </button>
            </div>
          ) : (
            <button type="button" className="linkbutton composer__addurdu" onClick={() => set('urdu', true)}>
              + Add an Urdu version
            </button>
          )}

          <div className="composer__settings">
            <div className="composer__setting">
              <span className="field__label" id="composer-importance">Importance</span>
              <div className="viewtoggle composer__importance" role="radiogroup" aria-labelledby="composer-importance">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    role="radio"
                    aria-checked={values.priority === p.value}
                    className={`viewtoggle__btn${values.priority === p.value ? ' viewtoggle__btn--on' : ''} composer__importance--${p.value.toLowerCase()}`}
                    onClick={() => set('priority', p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="settings__hint">{importance.hint}</p>
            </div>

            <SettingRow
              label="Who sees it"
              summary={describeAudience(values.audience, options)}
              open={open === 'audience'}
              onToggle={() => setOpen(open === 'audience' ? null : 'audience')}
              problem={attempted ? audienceIssue : null}
            >
              <AudiencePicker
                value={values.audience}
                onChange={(a) => set('audience', a)}
                options={options}
                canPickEveryFarm={allFarmsAllowed}
              />
            </SettingRow>

            <SettingRow
              label="When"
              summary={resolved.problem ? 'Needs a look' : describeWhen(values.when)}
              open={open === 'when'}
              onToggle={() => setOpen(open === 'when' ? null : 'when')}
              problem={resolved.problem}
            >
              <WhenPicker value={values.when} onChange={(w) => set('when', w)} />
            </SettingRow>
          </div>

          {error && <ErrorNote message={error} />}
        </div>

        <AnnouncementPreview
          item={previewItem}
          pictureSrc={pictureSrc}
          seenBy={describeAudience(values.audience, options)}
          shows={resolved.problem ? 'Not yet: check When' : describeWhen(values.when)}
        />
      </div>
    </Modal>
  );
}

/** One choice shown as a sentence, opened to change it. */
function SettingRow({
  label,
  summary,
  open,
  onToggle,
  problem,
  children,
}: {
  label: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  problem: string | null;
  children: ReactNode;
}) {
  return (
    <div className={`composer__setting${open ? ' composer__setting--open' : ''}`}>
      <div className="composer__settinghead">
        <span className="field__label">{label}</span>
        <span className="composer__summary">{summary}</span>
        <button type="button" className="btn btn--sm btn--ghost" aria-expanded={open} onClick={onToggle}>
          {open ? 'Done' : 'Change'}
        </button>
      </div>
      {problem && <p className="composer__problem">{problem}</p>}
      {open && <div className="composer__settingbody">{children}</div>}
    </div>
  );
}
